import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import usePageTitle from '../hooks/usePageTitle';
import { logger } from '../utils/logger';
import MarkdownContent from '../components/MarkdownContent';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import CanonicalLink from '../components/common/CanonicalLink';

// Section links for the left-hand menu
const sections = [
  { id: 'wavescoop-documentation', label: 'Overview', offset: 0 },
  { id: 'what-happens-under-the-hood', label: 'What happens under the hood', offset: -20 },
  { id: 'outputs-you-ll-see', label: 'Outputs you\'ll see', offset: -20 },
  { id: 'plan-limits', label: 'Plan limits', offset: -20 },
  { id: 'working-with-images', label: 'Working with images', offset: -20 },
  { id: 'security-privacy-at-a-glance', label: 'Security & Privacy', offset: -20 },
  { id: 'need-help', label: 'Need help?', offset: -20 },
];

const DocsPage: React.FC = () => {
  // Set page title
  usePageTitle('WaveScoop | Documentation');
  
  // Refs for sections
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  
  // State for active section
  const [activeSection, setActiveSection] = useState('overview');
  
  // Theme and responsive handling
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Documentation content in markdown format
  const documentationContent = `
## Overview  

WaveScoop lets you **preview public reaction** to any social-media post *before* you publish it.  
Drop in your draft text (and image), click **Simulate**, and WaveScoop shows:

* A one-glance **Audience Sentiment** gauge (0 = backlash, 100 = cheer squad)  
* Estimated **likes & replies** in the first day and first week  
* A sample thread of realistic comments—positive, negative, neutral—so you know what to expect  
* Clear improvement tips if the post looks risky

---

## What happens under the hood

* **Spot the moment**  
   WaveScoop first asks, *"What kind of post is this?"*—a product launch, a design reveal, a meme, an apology, etc.
* **Recall follower habits**  
   It remembers how your followers (and the wider platform) *usually* react to that kind of moment.  
   Are they excited, sceptical, or split?
* **Check the fit**  
   It compares your draft text + image to those habits.  
   Something way off-brand triggers a warning.
* **Estimate the mix**  
   Using those cues, it forecasts the share of **positive**, **negative**, and **neutral** replies it expects over the first week.
* **Grade its own certainty**  
   A big gap between positive and negative means *High* confidence.  
   If the crowd will likely be split, confidence is *Medium* or *Low*.

---

## Outputs you'll see

* **Sentiment Breakdown** - Visual chart showing the percentage split between positive, negative, and neutral reactions
* **Audience Sentiment Score** - Easy-to-read gauge showing overall sentiment on a 0-100 scale
* **Confidence Indicator** - Displays High/Medium/Low confidence in the prediction
* **Engagement Forecast** - Visual estimates of replies and likes at 1-day and 7-day marks
* **Reputation Impact** - Assessment of how the post might affect your brand reputation
* **Key Drivers** - Highlighted reasons for positive and negative reactions
* **Sample Replies** - A realistic thread of comments you can expect to receive
* **Improvement Tips** - Actionable suggestions when your post might need adjustment

---

## Plan limits

* **Trial**: 1 seat, 1 Account Slot, 10 simulations/month
* **Starter**: 3 seats, 3 Account Slots, 50 simulations/month
* **Pro**: 5 seats, 10 Account Slots, 250 simulations/month

\\*An **Account Slot** is one brand or creator handle you run simulations for.

Unused simulations reset on your billing date each month.

---

## Working with images

* WaveScoop's vision model writes its own alt-text and flags **Notable Deviations** (e.g., unexpected colours or oversized logos).
* Images auto-purge after 30 days on the Trial plan (retained indefinitely on paid tiers).

---

## Security & Privacy

* **Transport** - All requests over HTTPS/TLS 1.3
* **Data residency** - EU Postgres cluster by default
* **PII** - Emails and auth tokens stripped before LLM processing
* **Error tracking** - Sentry EU instance (no draft content in stack traces)

---

## Need help?

* **support@wavescoop.com** - Response time < 24 h weekdays

Happy simulating—may your next post ride the perfect wave! 🌊
  `;

  // Handle scrolling and intersection for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            if (id) setActiveSection(id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    // Create refs for section headings after initial render
    const headings = document.querySelectorAll('.markdown-content h1, .markdown-content h2');
    
    sectionRefs.current = Array.from(headings).map(heading => {
      const id = heading.textContent?.toLowerCase().replace(/[^\w]+/g, '-');
      if (id && heading instanceof HTMLElement) {
        heading.id = id;
        observer.observe(heading);
        return heading;
      }
      return null;
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Handle section click
  const handleSectionClick = (sectionId: string, label: string, offset: number) => {
    // Try finding by ID first
    let section = document.getElementById(sectionId);

    // Fallback: If ID fails, try finding by matching text content (more robust against timing/rendering issues)
    if (!section) {
      logger.warn(`Could not find section by ID "${sectionId}", trying text search for "${label}"`);
      const headings = document.querySelectorAll('.markdown-content h1, .markdown-content h2');
      section = Array.from(headings).find(h => h.textContent?.trim() === label) as HTMLElement | null;
    }

    if (section) {
      const position = section.getBoundingClientRect().top + window.pageYOffset + offset;
      window.scrollTo({
        top: position,
        behavior: 'smooth'
      });
    } else {
      // Log an error if both methods fail
      logger.error(`Failed to find section element for ID "${sectionId}" or label "${label}"`);
    }
  };

  return (
    <PublicPageLayout>
      <CanonicalLink />
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ p: { xs: 2, md: 4 }, pt: {xs: 2, md: 4} }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4,
              bgcolor: 'rgba(10, 30, 60, 0.85)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
              About WaveScoop
            </Typography>
            
            <Grid container spacing={4}>
              {/* Sidebar Navigation */}
              {!isMobile && (
                <Grid item xs={12} md={3}>
                  <Box sx={{ position: 'sticky', top: 100 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                      Contents
                    </Typography>
                    <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }} />
                    <List component="nav" dense sx={{ color: 'white' }}>
                      {sections.map((section) => (
                        <ListItem key={section.id} disablePadding>
                          <ListItemButton 
                            onClick={() => handleSectionClick(section.id, section.label, section.offset)}
                            sx={{ 
                              borderLeft: activeSection === section.id ? 
                                '3px solid #1976d2' : '3px solid transparent',
                              bgcolor: activeSection === section.id ? 
                                'rgba(25, 118, 210, 0.1)' : 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.05)'
                              }
                            }}
                          >
                            <ListItemText 
                              primary={section.label} 
                              primaryTypographyProps={{ 
                                color: 'white',
                                variant: 'body2',
                                fontWeight: activeSection === section.id ? 'bold' : 'normal'
                              }} 
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
              )}
              
              {/* Main Content */}
              <Grid item xs={12} md={9}>
                <Box className="markdown-wrapper">
                  <MarkdownContent content={documentationContent} />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </PublicPageLayout>
  );
};

export default DocsPage; 